import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { User } from 'lucide-react';
import { logoutInterview } from '../../../../auth/keycloak';
import { useInterviewAuthHint } from '../../../../auth/use-interview-auth-hint';
import { useTranslate } from '../../../../locales/hooks';

type Props = {
  forceVisible?: boolean;
  onLogout?: () => void | Promise<void>;
};

type CalloutPosition = {
  top: number;
  right: number;
};

const CALLOUT_OFFSET = 6;

const UserButton: React.FC<Props> = ({ forceVisible, onLogout }) => {
  const t = useTranslate();
  const isAuthenticated = useInterviewAuthHint();

  const [isCalloutOpen, setIsCalloutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [position, setPosition] = useState<CalloutPosition | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const calloutRef = useRef<HTMLDivElement>(null);

  const recalcPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPosition({
      top: rect.bottom + CALLOUT_OFFSET,
      right: window.innerWidth - rect.right,
    });
  }, []);

  const openCallout = useCallback(() => {
    recalcPosition();
    setIsCalloutOpen(true);
  }, [recalcPosition]);

  const closeCallout = useCallback(() => setIsCalloutOpen(false), []);

  useEffect(() => {
    if (!isCalloutOpen) return;

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (calloutRef.current?.contains(target)) return;
      setIsCalloutOpen(false);
    };

    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setIsCalloutOpen(false);
    };

    const onViewportChange = () => recalcPosition();

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDownCapture, { capture: true });
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDownCapture, { capture: true });
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [isCalloutOpen, recalcPosition]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await (onLogout ? onLogout() : logoutInterview());
    } catch (error) {
      console.error('Interview logout failed', error);
      setIsLoggingOut(false);
      setIsCalloutOpen(false);
    }
  }, [onLogout]);

  if (!forceVisible && !isAuthenticated) return null;

  return (
    <>
      <div className="flex items-center justify-center shrink-0">
        <button
          ref={buttonRef}
          onClick={() => (isCalloutOpen ? closeCallout() : openCallout())}
          className="relative flex items-center justify-center rounded-[8px] size-[40px] hover:bg-gray-100 transition-colors"
          aria-label={t('userButton__ariaLabel')}
          aria-haspopup="dialog"
          aria-expanded={isCalloutOpen}
        >
          <User className="w-[20px] h-[20px] text-black" />
        </button>
      </div>

      {isCalloutOpen && position && createPortal(
        <div
          ref={calloutRef}
          role="dialog"
          aria-label={t('userButton__calloutTitle')}
          style={{ position: 'fixed', top: position.top, right: position.right }}
          className="z-[10000] w-[260px] rounded-[10px] border border-[#ddd] bg-white shadow-lg p-[14px] flex flex-col gap-[12px]"
        >
          <p className="font-['Manrope:Medium',sans-serif] font-medium leading-[1.4] text-[#0f172a] text-[14px]">
            {t('userButton__calloutTitle')}
          </p>
          <div className="flex items-center justify-end gap-[8px]">
            <button
              onClick={closeCallout}
              disabled={isLoggingOut}
              className="px-[12px] py-[6px] rounded-[8px] border border-[#ddd] hover:bg-[#f8fafc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-['Manrope:Medium',sans-serif] font-medium text-[#334155] text-[13px]">
                {t('userButton__cancelButton')}
              </span>
            </button>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="px-[12px] py-[6px] bg-[#0f172a] rounded-[8px] hover:bg-[#334155] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-['Manrope:Medium',sans-serif] font-medium text-white text-[13px]">
                {t('userButton__logoutButton')}
              </span>
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default UserButton;
